import React, { useEffect, useRef } from "react";
import { CardBody, CardHeader } from "reactstrap";
import { Chart } from "chart.js/auto";
import moment from 'moment';
import Skeleton from "react-loading-skeleton";
import domain from 'src/config/domain';
import {
  createGradient,
  chartColors,
  richTooltipConfig,
  richAnimationConfig,
} from 'src/config/chart';

const SaleRevenue = ({ data, loading }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const domainConfig = domain();

  useEffect(() => {
    if (loading === false && canvasRef.current) {
      // Destroy previous chart if exists
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      const ctx = canvasRef.current.getContext("2d");
      const colors = domainConfig.isAcquirerFlow ? chartColors.blue : chartColors.violet;
      const gradient = createGradient(ctx, colors.gradientStart, colors.gradientEnd);

      var config = {
        type: "line",
        data: {
          labels: data.year_breakdown.map((item) => moment(new Date(item.txn_month)).format('MMM')),
          datasets: [
            {
              label: "Bookings",
              fill: true,
              backgroundColor: gradient,
              borderColor: colors.primary,
              borderWidth: 2.5,
              tension: 0.4,
              pointRadius: 4,
              pointBackgroundColor: '#fff',
              pointBorderColor: colors.primary,
              pointBorderWidth: 2,
              pointHoverRadius: 8,
              pointHoverBackgroundColor: '#fff',
              pointHoverBorderColor: colors.primary,
              pointHoverBorderWidth: 3,
              pointHitRadius: 20,
              data: data.year_breakdown.map((item) => item.monthly_sum),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          hover: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              ...richTooltipConfig,
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  return ` ${new Intl.NumberFormat('en-GB').format(value)} bookings`;
                },
              },
            },
          },
          animation: richAnimationConfig,
          scales: {
            y: {
              display: true,
              grid: {
                display: true,
                color: 'rgba(148, 163, 184, 0.08)',
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 11,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 12,
                maxTicksLimit: 5,
              },
            },
            x: {
              display: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 10,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 8,
                maxTicksLimit: 12,
                maxRotation: 0
              },
            },
          },
        },
      };

      chartRef.current = new Chart(ctx, config);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    }
  }, [loading, data, domainConfig.isAcquirerFlow]);

  if (loading) {
    return (
      <div className="flex flex-col rounded-md border animate-in fade-in-50 mt-2">
        <CardHeader className="bg-gray-50 mb-2">
          <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
            Bookings <span className="font-bold">Overview</span>
          </h2>
        </CardHeader>


        <CardBody className="card-inner pt-0">
          <div className="text-center mb-1 fs-20px w-25 float-left pr-4">
            <Skeleton count={1} height="50px" />
          </div>
          <div className="text-center mb-1 fs-20px w-25 float-left pr-4">
            <Skeleton count={1} height="50px" />
          </div>
          <div className="text-center mb-1 fs-20px w-25 float-left">
            <Skeleton count={1} height="50px" />
          </div>
          <div className="text-center fs-20px w-100">
            <Skeleton count={1} height="200px" />
          </div>
        </CardBody>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-md border animate-in fade-in-50 mt-2">
      <CardHeader className="bg-gray-50 mb-2 text-violet-800">
        <div className="float-right text-sm pt-0.5">
          <span className={`inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-md text-xs font-medium border ${domainConfig.styles.borderColour} ${domainConfig.styles.lightBGColour} ${domainConfig.styles.textColour}`}>Month on Month</span>
        </div>
        <h2 className={`text-xl ${domainConfig.styles.textColour}`}>
          Bookings <span className="font-bold">Overview</span>
        </h2>
      </CardHeader>

      <CardBody className="card-inner pt-0">
        <div className="nk-sale-data-group flex-md-nowrap g-4 w-100">
          <div className="nk-sale-data min-w-[150px]" stlye={{ minWidth: '125px' }}>
            <span className="amount">
              {new Intl.NumberFormat('en-GB').format(data.day)}{" "}
            </span>
            <span className="sub-title">Today</span>
          </div>
          <div className="nk-sale-data min-w-[150px]" stlye={{ minWidth: '125px' }}>
            <span className="amount">
            {new Intl.NumberFormat('en-GB').format(data.week)}{" "}
            </span>
            <span className="sub-title">This Week</span>
          </div>
          <div className="nk-sale-data min-w-[150px]" stlye={{ minWidth: '125px' }}>
            <span className="amount">
            {new Intl.NumberFormat('en-GB').format(data.month)}{" "}
            </span>
            <span className="sub-title">This Month</span>
          </div>
        </div>
        <div className="pt-4 grow max-h-[250px]">
          <canvas ref={canvasRef}></canvas>
        </div>
      </CardBody>
    </div>
  );
};
export default SaleRevenue;
